import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import RequestCard from '../components/lifelink/RequestCard';
import { PriorityBadge, StatusBadge } from '../components/lifelink/PriorityBadge';
import { CATEGORY_CONFIG } from '../components/lifelink/CategoryGrid';
import FileGallery from '../components/lifelink/FileGallery';
import { List, Filter, ChevronLeft, ChevronRight, MapPin, Clock, User } from 'lucide-react';
import axios from 'axios';
import useSEO from '../hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RequestsPage() {
  const { t } = useLanguage();
  useSEO({
    title: 'Richieste di Emergenza',
    description: 'Lista completa delle richieste di aiuto attive su LIFE LINKE. Filtra per categoria, priorità e stato. Paginazione e dettagli per ogni richiesta.',
    keywords: 'richieste emergenza, lista aiuti, soccorso attivo, filtra emergenze',
  });
  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({ categoria: '', priorita: '', stato: '' });
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const limit = 20;

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, limit, skip: page * limit };
      const { data } = await axios.get(`${API}/requests`, { params });
      setRequests(data.requests || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-[#E8F4FD] pt-20 pb-8" data-testid="requests-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-6">
          <List size={20} className="text-[#FF3B30]" />
          <h1 className="font-['Barlow_Condensed'] text-2xl sm:text-3xl font-black uppercase tracking-tight text-[#0D1B2A]">
            {t('richieste')}
          </h1>
          <span className="text-xs font-mono text-[#6B8AB5] uppercase tracking-widest">{total} totali</span>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-6 flex-wrap" data-testid="requests-filters">
          <Filter size={14} className="text-[#4A6B8A]" />
          <select
            value={filters.stato}
            onChange={(e) => { setFilters(f => ({ ...f, stato: e.target.value })); setPage(0); }}
            className="bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-2 text-xs uppercase tracking-widest focus:border-[#FF3B30] focus:outline-none"
            data-testid="requests-filter-stato"
          >
            <option value="">{`Stato: ${t('tutti')}`}</option>
            {['nuova', 'in_corso', 'assegnata', 'risolta', 'chiusa'].map(s => (
              <option key={s} value={s}>{t(s)}</option>
            ))}
          </select>
          <select
            value={filters.priorita}
            onChange={(e) => { setFilters(f => ({ ...f, priorita: e.target.value })); setPage(0); }}
            className="bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-2 text-xs uppercase tracking-widest focus:border-[#FF3B30] focus:outline-none"
            data-testid="requests-filter-priorita"
          >
            <option value="">{`${t('priorita')}: ${t('tutti')}`}</option>
            {['critica', 'alta', 'media', 'bassa'].map(p => (
              <option key={p} value={p}>{t(p)}</option>
            ))}
          </select>
          <select
            value={filters.categoria}
            onChange={(e) => { setFilters(f => ({ ...f, categoria: e.target.value })); setPage(0); }}
            className="bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-2 text-xs uppercase tracking-widest focus:border-[#FF3B30] focus:outline-none"
            data-testid="requests-filter-categoria"
          >
            <option value="">{`Categoria: ${t('tutti')}`}</option>
            {CATEGORY_CONFIG.map(c => (
              <option key={c.id} value={c.id}>{t(c.id)}</option>
            ))}
          </select>
        </div>

        {/* Requests */}
        {loading ? (
          <div className="text-center py-12 text-[#6B8AB5] text-sm">{t('caricamento')}</div>
        ) : requests.length === 0 ? (
          <div className="text-center py-12 text-[#6B8AB5] text-sm border border-[#B8D4E8] bg-white">{t('nessun_risultato')}</div>
        ) : (
          <div className="space-y-3">
            {requests.map(req => (
              <RequestCard key={req.id} request={req} onClick={() => setSelectedRequest(req)} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-6" data-testid="pagination">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="p-2 border border-[#B8D4E8] hover:border-[#FF3B30] disabled:opacity-30 transition-all"
              data-testid="prev-page-btn"
            >
              <ChevronLeft size={16} className="text-[#0D1B2A]" />
            </button>
            <span className="text-xs font-mono text-[#4A6B8A] uppercase tracking-widest">
              {page + 1} / {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="p-2 border border-[#B8D4E8] hover:border-[#FF3B30] disabled:opacity-30 transition-all"
              data-testid="next-page-btn"
            >
              <ChevronRight size={16} className="text-[#0D1B2A]" />
            </button>
          </div>
        )}

        {/* Detail */}
        {selectedRequest && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setSelectedRequest(null)} data-testid="request-detail-overlay">
            <div className="bg-white border border-[#B8D4E8] w-full max-w-lg max-h-[80vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
              <div className="flex items-center gap-3 mb-4">
                {(() => {
                  const cat = CATEGORY_CONFIG.find(c => c.id === selectedRequest.categoria);
                  const Icon = cat?.icon;
                  return Icon ? <Icon size={24} style={{ color: cat.color }} /> : null;
                })()}
                <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-[#0D1B2A]">
                  {t(selectedRequest.categoria)}
                </h2>
              </div>
              <div className="flex gap-2 mb-4">
                <PriorityBadge priority={selectedRequest.priorita} />
                <StatusBadge status={selectedRequest.stato} />
              </div>
              <p className="text-sm text-[#4A6B8A] mb-4">{selectedRequest.descrizione}</p>
              <div className="space-y-2 text-xs text-[#6B8AB5]">
                {selectedRequest.indirizzo && (
                  <div className="flex items-center gap-2"><MapPin size={12} />{selectedRequest.indirizzo}</div>
                )}
                {selectedRequest.nome_richiedente && (
                  <div className="flex items-center gap-2"><User size={12} />{selectedRequest.nome_richiedente}</div>
                )}
                <div className="flex items-center gap-2">
                  <Clock size={12} />{new Date(selectedRequest.created_at).toLocaleString('it-IT')}
                </div>
              </div>
              {/* File attachments */}
              <FileGallery requestId={selectedRequest.id} />
              <button
                onClick={() => setSelectedRequest(null)}
                className="mt-4 w-full py-2 border border-[#B8D4E8] text-[#4A6B8A] text-xs uppercase tracking-widest hover:border-[#FF3B30] hover:text-[#0D1B2A] transition-all"
                data-testid="close-detail-btn"
              >
                {t('annulla')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
